import arrayMutators from 'final-form-arrays';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { Redirect, RouteComponentProps, withRouter } from 'react-router-dom';

import { Button } from '@xsolla/ui-core';
import { ArrowLeft } from '@xsolla/ui-icons';

import {
  getCustomScopes,
  putCustomScopes,
  setSocialConnections,
  SocialConnection,
  updateSocialConnections
} from '../../../api';
import { ChecklistItems } from '../../../api/configurations/checklist/types';
import { Error } from '../../../api/setup/getApiError';
import { SocialSettingsHeader, SocialSettingsMainBox } from '../../../components';
import scopeValues from '../../../components/Authentication/SocialSettingsMainBox/scopeValues';
import { Scope } from '../../../components/Authentication/SocialSettingsMainBox/scopeValues/types';
import { FormValues } from '../../../components/Authentication/SocialSettingsMainBox/types';
import { socialSettingsValidator } from '../../../components/Authentication/SocialSettingsMainBox/validator';
import { FormFooter, FormHeader, Preloader, ToNavigationError } from '../../../components/ui';
import { useConfig } from '../../../contexts/config';
import { SettingsContext, useChecklist } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { HorizontalCenterBlock } from '../../../styled';
import { BoldWrapper, MainContent } from './style';

type Props = RouteComponentProps & {
  socialName: string;
  projectId: string;
};

const defaultScope: Scope = {
  name: '',
  required: false,
  value: ''
};

const SocialSettingsPagePure = ({ projectId, socialName, history }: Props): JSX.Element => {
  const intl = useIntl();
  const { currentSocials } = useContext(SettingsContext);
  const { xsollaAnalytics } = useConfig();
  const { socialConnectionPath } = usePaths();
  const [fetching, setFetching] = useState<boolean>(false);
  const [fetchingScopes, setFetchingScopes] = useState<boolean>(false);
  const [scopesError, setScopesError] = useState<Error | null>(null);
  const [socialSettings, setSocialSettings] = useState<SocialConnection>({
    configurable_fields: [],
    connected: false,
    is_custom: false,
    provider: ''
  });

  const { enableChecklistItem } = useChecklist(projectId);
  const [scopes, setScopes] = useState<string[]>([]);
  const [refreshAlways, setRefreshAlways] = useState<boolean>(true);
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const scopesMapNames: Record<string, string> = useMemo(() => {
    const { userData = [defaultScope], permissions = [defaultScope] } =
      scopeValues?.[socialName] || {};
    const allScopes: Scope[] = [...userData, ...permissions];

    return {
      ...allScopes.reduce((prevData, data) => ({ ...prevData, [data.name]: data.value }), {}),
      ...allScopes.reduce((prevData, data) => ({ ...prevData, [data.value]: data.name }), {})
    };
  }, [socialName]);

  const scopesRequired: Record<string, boolean> = useMemo(() => {
    const { userData = [defaultScope], permissions = [defaultScope] } =
      scopeValues?.[socialName] || {};
    const allScopes: Scope[] = [...userData, ...permissions];

    return allScopes.reduce((prevValue, scope) => {
      if (scope.required) {
        return { ...prevValue, [scope.name]: true };
      }
      return prevValue;
    }, {});
  }, [socialName]);

  useEffect(() => {
    if (currentSocials) {
      const currentSocialObject = currentSocials.find((item) => item.provider === socialName);
      setSocialSettings(currentSocialObject as SocialConnection);
    }
  }, [currentSocials]);

  useEffect(() => {
    if (socialName === 'xsolla-id') return;
    if (currentSocials && currentSocials.find((item) => item.provider === socialName)?.connected) {
      setFetchingScopes(true);
      getCustomScopes(projectId, socialName)
        .then((res) => {
          setScopes(res.custom_scope);
          setRefreshAlways(res.refresh_always);
        })
        .catch((err) => setScopesError(err))
        .finally(() => setFetchingScopes(false));
    }
  }, [currentSocials]);

  const mapValues = useMemo(() => {
    if (socialSettings.configurable_fields.length === 0) {
      return null;
    }

    let fieldsValue = {};
    let scopesValues = {};

    fieldsValue = socialSettings.configurable_fields.reduce((prevValue, field) => {
      return {
        ...prevValue,
        [field.name]: field.value
      };
    }, {});

    if (scopes) {
      scopesValues = scopes.reduce((previousValue, currentValue) => {
        if (currentValue) {
          return {
            ...previousValue,
            [scopesMapNames[currentValue]]: true
          };
        }
        return previousValue;
      }, {});
    }

    return {
      ...fieldsValue,
      scopesValues: { ...scopesValues, ...scopesRequired },
      refresh_always: refreshAlways
    };
  }, [socialSettings, scopes, refreshAlways]);

  if (socialName === 'xsolla-id') {
    return <Redirect to={socialConnectionPath(projectId)} />;
  }

  const onSubmit = async (values: FormValues) => {
    const newValues: FormValues = {
      ...values,
      audience: [values.audience]
    };

    try {
      setFetching(true);
      const { refresh_always, scopesValues, ...restFields } = newValues;
      const isXsollaKeys = !restFields.client_id && !restFields.client_secret;

      const submitValues = {
        ...socialSettings.configurable_fields.reduce(
          (prevValue, field) => ({ ...prevValue, [field.name]: restFields[field.name] || null }),
          {}
        ),
        one_tap_login: { enabled: !!socialSettings.one_tap_login?.enabled }
      };

      const submitScopeValues = Object.entries(scopesValues || {})
        .filter(([_key, value]) => value)
        .map(([key, _value]) => scopesMapNames[key]);

      if (socialSettings.connected) {
        await updateSocialConnections({
          projectId,
          provider: socialSettings.provider,
          values: submitValues
        });

        if (!isXsollaKeys && socialSettings.provider !== 'wbgames') {
          await putCustomScopes({
            projectId,
            providerName: socialSettings.provider,
            refresh_always,
            custom_scope: submitScopeValues
          });
        }
      } else {
        await setSocialConnections({
          projectId,
          provider: socialSettings.provider,
          values: submitValues
        });
        await enableChecklistItem(ChecklistItems.CONNECTED_SOCIAL);

        if (!isXsollaKeys && socialSettings.provider !== 'wbgames') {
          await putCustomScopes({
            projectId,
            providerName: socialSettings.provider,
            refresh_always,
            custom_scope: submitScopeValues
          });
        }
      }
      const newConfigurableFields = socialSettings.configurable_fields.map((field) => {
        return {
          ...field,
          value: newValues[field.name] as string
        };
      });

      setSocialSettings((prev) => ({
        ...prev,
        connected: true,
        configurable_fields: newConfigurableFields
      }));

      xsollaAnalytics.sendClick('login_pa_module__social-connection__connect', projectId, {
        ...newValues,
        social: socialName,
        scopes: submitScopeValues,
        client_secret: newValues.client_secret ? '***' : null
      });

      showSuccessNotify();
    } catch (e) {
      showErrorNotify(e);
    } finally {
      setFetching(false);
    }
  };

  const backToConnections = () => history.push({ pathname: socialConnectionPath(projectId) });

  if (fetchingScopes || socialSettings.configurable_fields.length === 0) {
    return <Preloader />;
  }

  if (scopesError) {
    return <ToNavigationError error={scopesError} pageName="socials-settings_page" />;
  }

  return (
    <Form
      autoComplete="off"
      onSubmit={onSubmit}
      initialValues={mapValues || {}}
      validate={socialSettingsValidator(socialSettings)}
      mutators={{
        ...arrayMutators
      }}
      keepDirtyOnReinitialize
      render={({ handleSubmit, pristine, hasValidationErrors, submitFailed }) => (
        <form onSubmit={handleSubmit}>
          <FormHeader>
            <Button
              look="link"
              palette="muted"
              data-test-id="social-settings-back-button"
              onClick={backToConnections}>
              <ArrowLeft />
              <span>
                {intl.formatMessage(
                  { id: 'social-connection.settings.back-button' },
                  {
                    b: (chunk: string) => <BoldWrapper>{chunk}</BoldWrapper>
                  }
                )}
              </span>
            </Button>
            <HorizontalCenterBlock>
              <MainContent>
                <SocialSettingsHeader
                  socialSettings={socialSettings}
                  setSocialSettings={setSocialSettings}
                />
              </MainContent>
            </HorizontalCenterBlock>
          </FormHeader>
          <SocialSettingsMainBox socialSettings={socialSettings} />

          <FormFooter
            onSubmit={() => onSubmit}
            onCancel={backToConnections}
            fetching={fetching}
            error={submitFailed && hasValidationErrors}
            submitText={intl.formatMessage({
              id: 'social-connection.mode.connect'
            })}
            disabled={pristine}
          />
        </form>
      )}
    />
  );
};

export const SocialSettingsPage = withRouter(SocialSettingsPagePure);
